import type { OHLCVBar } from '@/types';

// ── Day-index alignment ───────────────────────────────────────────────────────
// Returns a Map keyed by integer day index (floor(unix_sec / 86400)) → close price.
// Used for O(1) benchmark lookups inside hot loops.
export function alignByDay(bars: OHLCVBar[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const bar of bars) {
    map.set(Math.floor(bar.time / 86400), bar.close);
  }
  return map;
}

// ── RS line scaling ───────────────────────────────────────────────────────────
// For each stock bar, computes:
//   rsRaw[i]    = stockClose[i] / benchClose[i]
//   rsScaled[i] = (rsRaw[i] / rsRaw[ref]) * stockClose[ref] * offsetPct
// where ref is the LATEST bar where both stock and benchmark have a valid price.
// Returns Map<bar.time (unix_sec), rsScaled> — only bars with a valid benchmark.
export function computeRSMap(
  stockBars: OHLCVBar[],
  benchmarkDayMap: Map<number, number>,
  offsetPct: number,
): Map<number, number> {
  const out = new Map<number, number>();
  if (!stockBars.length) return out;

  // Find the reference point — LATEST bar where benchmark also has data.
  // Using the latest bar means scale = latestBenchClose × offsetPct, which
  // places the RS line at stockClose × offsetPct on the most recent bar —
  // identical to MarketSmith/Pine Script's SpxValue × offset% approach.
  let refRsRaw      = 0;
  let refStockClose = 0;
  for (let i = stockBars.length - 1; i >= 0; i--) {
    const bar        = stockBars[i];
    const dayKey     = Math.floor(bar.time / 86400);
    const benchClose = benchmarkDayMap.get(dayKey);
    if (benchClose && benchClose > 0 && bar.close > 0) {
      refRsRaw      = bar.close / benchClose;
      refStockClose = bar.close;
      break;
    }
  }
  if (refRsRaw === 0 || refStockClose === 0) return out;

  const scale = refStockClose * offsetPct / refRsRaw;

  for (const bar of stockBars) {
    const dayKey    = Math.floor(bar.time / 86400);
    const benchClose = benchmarkDayMap.get(dayKey);
    if (!benchClose || benchClose <= 0 || bar.close <= 0) continue;
    const rsRaw    = bar.close / benchClose;
    out.set(bar.time, rsRaw * scale);
  }

  return out;
}

// ── EMA of a time-keyed series ───────────────────────────────────────────────
// Seeds with the SMA of the first `period` values, then applies the standard
// EMA recurrence. Returns Map<time, ema> starting at index period-1.
export const DEFAULT_RS_EMA_PERIOD = 21;

export function clampEmaPeriod(period: number | undefined): number {
  const p = Math.round(period ?? DEFAULT_RS_EMA_PERIOD);
  if (!Number.isFinite(p)) return DEFAULT_RS_EMA_PERIOD;
  return Math.min(200, Math.max(2, p));
}

export function computeEMAMap(series: Map<number, number>, period: number): Map<number, number> {
  const out = new Map<number, number>();
  const entries = Array.from(series.entries()).sort((a, b) => a[0] - b[0]);
  if (entries.length < period) return out;

  const k = 2 / (period + 1);
  let sum = 0;
  for (let i = 0; i < period; i++) sum += entries[i][1];
  let ema = sum / period;
  out.set(entries[period - 1][0], ema);

  for (let i = period; i < entries.length; i++) {
    ema = entries[i][1] * k + ema * (1 - k);
    out.set(entries[i][0], ema);
  }
  return out;
}

// ── RS Rating (1–99) ─────────────────────────────────────────────────────────
// Weighted 4-quarter relative performance vs benchmark, converted to a 1–99
// percentile band score matching MarketSmith-style methodology.
// Returns 0 when insufficient data (< 63 daily bars).
export function computeRSRating(
  stockDailyBars: OHLCVBar[],
  benchmarkDailyBars: OHLCVBar[],
): number {
  const N = stockDailyBars.length;
  if (N < 63) return 0;

  // Align benchmark by day
  const benchMap = alignByDay(benchmarkDailyBars);

  // Get the latest day's closes
  const latestStock = stockDailyBars[N - 1];

  // Helper: stock close at N-lookback days ago
  function stockAt(lookback: number): number {
    const idx = N - 1 - lookback;
    if (idx < 0) return 0;
    return stockDailyBars[idx].close;
  }

  // Helper: benchmark close at N-lookback days ago (by day index from latest)
  function benchAt(lookback: number): number {
    const idx = N - 1 - lookback;
    if (idx < 0) return 0;
    const dayKey = Math.floor(stockDailyBars[idx].time / 86400);
    return benchMap.get(dayKey) ?? 0;
  }

  const latestBench = benchAt(0);
  if (!latestStock.close || !latestBench) return 0;

  // 4-quarter periods
  const periods = [63, 126, 189, 252] as const;
  const weights = [0.4, 0.2, 0.2, 0.2] as const;

  let rsStock = 0;
  let rsBench = 0;

  for (let qi = 0; qi < 4; qi++) {
    const lb = periods[qi];
    if (N - 1 < lb) break; // fewer bars than this period — skip remaining quarters

    const prevStock = stockAt(lb);
    const prevBench = benchAt(lb);
    if (!prevStock || !prevBench) break;

    rsStock += weights[qi] * (latestStock.close / prevStock);
    rsBench += weights[qi] * (latestBench     / prevBench);
  }

  if (rsBench === 0) return 0;

  const rsScore = (rsStock / rsBench) * 100;

  // ── Band interpolation → 1–99 ────────────────────────────────────────────
  type Band = [lo: number, hi: number, ratingLo: number, ratingHi: number];
  const bands: Band[] = [
    [200, Infinity, 99,  99],
    [120, 200,      90,  98],
    [100, 120,      70,  89],
    [ 90, 100,      50,  69],
    [ 80,  90,      30,  49],
    [ 55,  80,      10,  29],
    [  0,  55,       1,   9],
  ];

  for (const [lo, hi, ratingLo, ratingHi] of bands) {
    if (rsScore >= lo) {
      if (ratingLo === ratingHi) return ratingLo; // top band
      const frac = Math.min((rsScore - lo) / (hi - lo), 1);
      return Math.round(ratingLo + frac * (ratingHi - ratingLo));
    }
  }

  return 1;
}
